import { getBuildTimeDefaultFormat } from './buildTimeConfig';
import { 
  getDateFormatConfig as getDateFormatConfigApi, 
  updateDateFormatConfig as updateDateFormatConfigApi
} from '../api/General/dateFormatConfig';

/**
 * Centralized date format configuration for the entire Mowazaf application
 * This file contains all date, time, and datetime formats used throughout the project
 * Configuration is now stored per company in the database
 */

export interface DateFormats {
  // Basic date formats
  date: {
    display: string;        // For displaying dates to users (e.g., in tables, cards)
    input: string;          // For HTML date inputs
    api: string;            // For API requests and database storage
    short: string;          // For compact displays
    long: string;           // For detailed displays
  };
  
  // Time formats
  time: {
    display: string;        // For displaying time to users
    input: string;          // For HTML time inputs
    short: string;          // For compact time displays (HH:mm)
    long: string;           // For detailed time displays with seconds
    ampm: string;           // 12-hour format with AM/PM
  };
  
  // DateTime formats
  datetime: {
    display: string;        // For displaying datetime to users
    input: string;          // For HTML datetime-local inputs
    api: string;            // For API requests (ISO format)
    short: string;          // For compact datetime displays
    long: string;           // For detailed datetime displays
  };
  
  // Specialized formats
  specialized: {
    monthYear: string;      // For month/year pickers (MM/YYYY)
    dayMonth: string;       // For day/month displays (DD MMM)
    weekday: string;        // For weekday displays
    relativeDays: string;   // For relative day formats
  };
}

// Default date formats configuration
export const DEFAULT_DATE_FORMATS: DateFormats = {
  // Owner decision 2026-09-17: ONE format across the whole system, never the viewer's machine locale.
  // Dates dd-mm-yyyy, times 24-hour HH:mm. `input`/`api` stay ISO — they are wire formats, not display.
  date: {
    display: "DD-MM-YYYY",
    input: "YYYY-MM-DD",
    api: "YYYY-MM-DD",
    short: "DD-MM-YY",
    long: "dddd, DD-MM-YYYY",
  },
  time: {
    display: "HH:mm",
    input: "HH:mm",
    short: "HH:mm",
    long: "HH:mm:ss",
    ampm: "HH:mm",
  },
  datetime: {
    display: "DD-MM-YYYY HH:mm",
    input: "YYYY-MM-DDTHH:mm",
    api: "YYYY-MM-DDTHH:mm:ss.SSSZ",
    short: "DD-MM HH:mm",
    long: "dddd, DD-MM-YYYY [at] HH:mm",
  },
  specialized: {
    monthYear: "MM-YYYY",
    dayMonth: "DD-MM",
    weekday: "dddd",
    relativeDays: "dddd, DD-MM",
  },
};

// Alternative format configurations for different locales/preferences
export const US_DATE_FORMATS: DateFormats = {
  date: {
    display: "MM/DD/YYYY",
    input: "YYYY-MM-DD",
    api: "YYYY-MM-DD",
    short: "MM/DD/YY",
    long: "dddd, MMMM DD, YYYY",
  },
  time: {
    display: "hh:mm A",
    input: "HH:mm",
    short: "hh:mm A",
    long: "hh:mm:ss A",
    ampm: "hh:mm A",
  },
  datetime: {
    display: "MM/DD/YYYY hh:mm A",
    input: "YYYY-MM-DDTHH:mm",
    api: "YYYY-MM-DDTHH:mm:ss.SSSZ",
    short: "MM/DD hh:mm A",
    long: "dddd, MMMM DD, YYYY [at] hh:mm A",
  },
  specialized: {
    monthYear: "MM/YYYY",
    dayMonth: "MMM DD",
    weekday: "dddd",
    relativeDays: "dddd, MMM DD",
  },
};

export const ISO_DATE_FORMATS: DateFormats = {
  date: {
    display: "YYYY-MM-DD",
    input: "YYYY-MM-DD",
    api: "YYYY-MM-DD",
    short: "YYYY-MM-DD",
    long: "dddd, YYYY-MM-DD",
  },
  time: {
    display: "HH:mm",
    input: "HH:mm",
    short: "HH:mm",
    long: "HH:mm:ss",
    ampm: "HH:mm",
  },
  datetime: {
    display: "YYYY-MM-DD HH:mm",
    input: "YYYY-MM-DDTHH:mm",
    api: "YYYY-MM-DDTHH:mm:ss.SSSZ",
    short: "YYYY-MM-DD HH:mm",
    long: "dddd, YYYY-MM-DD [at] HH:mm",
  },
  specialized: {
    monthYear: "YYYY-MM",
    dayMonth: "MM-DD",
    weekday: "dddd",
    relativeDays: "dddd, YYYY-MM-DD",
  },
};

// Configuration type for the application
export type DateFormatConfig = 'default' | 'us' | 'iso' | 'custom';

// Format configurations map
export const DATE_FORMAT_CONFIGS: Record<DateFormatConfig, DateFormats> = {
  default: DEFAULT_DATE_FORMATS,
  us: US_DATE_FORMATS,
  iso: ISO_DATE_FORMATS,
  custom: DEFAULT_DATE_FORMATS, // Will be overridden by user settings
};

// Current active configuration (can be set via environment variable or user preference)
let currentConfig: DateFormatConfig = 'default';
let customFormats: DateFormats | null = null;

/**
 * Get the current date formats configuration
 */
export const getDateFormats = (): DateFormats => {
  const base = DEFAULT_DATE_FORMATS;
  const raw = (currentConfig === 'custom' && customFormats) ? customFormats : DATE_FORMAT_CONFIGS[currentConfig];
  if (!raw) return base;
  return {
    date: { ...base.date, ...(raw as any).date },
    time: { ...base.time, ...(raw as any).time },
    datetime: { ...base.datetime, ...(raw as any).datetime },
    specialized: { ...base.specialized, ...(raw as any).specialized },
  };
};

/**
 * Set the current date format configuration
 */
export const setDateFormatConfig = (config: DateFormatConfig, custom?: DateFormats) => {
  currentConfig = config;
  if (config === 'custom' && custom) {
    customFormats = custom;
  }
};

export const getCurrentDateFormatConfig = (): DateFormatConfig => currentConfig;

/**
 * Initialize date format configuration from the database
 */
export const initializeDateFormats = async (companyId?: number, skipSave: boolean = true): Promise<void> => {
  // Only run in browser environment
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    // Try to get from API first
  const response = await getDateFormatConfigApi(companyId);
    
  if (response.status === 200 && response.payload) {
      const config = response.payload;
      if (config.configType === 'custom' && config.customFormats) {
        try {
          const custom = JSON.parse(config.customFormats) as DateFormats;
          setDateFormatConfig(config.configType, custom);
        } catch (e) {
          setDateFormatConfig('default');
        }
      } else {
        setDateFormatConfig(config.configType);
      }
  } else {
      // Fallback to build-time configuration if API fails
      const defaultFormat = getBuildTimeDefaultFormat();
      setDateFormatConfig(defaultFormat);
    }
  } catch (error) {
    
    // Try to get from localStorage as final fallback for migration purposes
    const savedConfig = localStorage.getItem('mowazaf_date_format_config');
    if (savedConfig) {
      const config = savedConfig as DateFormatConfig;
      if (config === 'custom') {
        const savedCustomFormats = localStorage.getItem('mowazaf_custom_date_formats');
        if (savedCustomFormats) {
          try {
            const custom = JSON.parse(savedCustomFormats) as DateFormats;
            setDateFormatConfig(config, custom);
            // Skip saving during initialization unless explicitly allowed
            if (!skipSave) {
              // Try to save to database for future use
              saveDateFormatConfig(config, custom, companyId).catch(() => {
              });
            }
          } catch (e) {
            setDateFormatConfig('default');
          }
        } else {
          setDateFormatConfig(config);
        }
      } else {
        setDateFormatConfig(config);
        // Skip saving during initialization unless explicitly allowed
        if (!skipSave) {
          // Try to save to database for future use
          saveDateFormatConfig(config, undefined, companyId).catch(() => {
            console.warn('Failed to migrate localStorage config to database');
          });
        }
      }
    } else {
      // Final fallback to build-time configuration
      const defaultFormat = getBuildTimeDefaultFormat();
      setDateFormatConfig(defaultFormat);
    }
  }
};

/**
 * Save current configuration to the database
 */
export const saveDateFormatConfig = async (config: DateFormatConfig, custom?: DateFormats, companyId?: number): Promise<boolean> => {
  try {
  const response = await updateDateFormatConfigApi({
      configType: config,
      customFormats: custom
  }, companyId);
    
    if (response.status === 200) {
      setDateFormatConfig(config, custom);
      
      // Also save to localStorage for offline fallback
      localStorage.setItem('mowazaf_date_format_config', config);
      if (config === 'custom' && custom) {
        localStorage.setItem('mowazaf_custom_date_formats', JSON.stringify(custom));
      } else {
        localStorage.removeItem('mowazaf_custom_date_formats');
      }
      
      return true;
    } else {
      console.error('Failed to save date format configuration:', response.errors);
      return false;
    }
  } catch (error) {
    console.error('Error saving date format configuration:', error);
    
    // Fallback to localStorage only
    localStorage.setItem('mowazaf_date_format_config', config);
    if (config === 'custom' && custom) {
      localStorage.setItem('mowazaf_custom_date_formats', JSON.stringify(custom));
    }
    setDateFormatConfig(config, custom);
    
    return false;
  }
};

/**
 * Reset configuration to default on server for a specific company (or current user's company)
 */
// Note: Server-side reset endpoint wrapper was removed as unused; hook uses updateConfig('default').

// Note: No automatic initialization on import to avoid unintended API calls/writes.
